import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GlobalStyles from '@mui/joy/GlobalStyles';
import Avatar from '@mui/joy/Avatar';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Chip from '@mui/joy/Chip';
import Divider from '@mui/joy/Divider';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemButton from '@mui/joy/ListItemButton';
import ListItemContent from '@mui/joy/ListItemContent';
import ListItemDecorator from '@mui/joy/ListItemDecorator';
import Sheet from '@mui/joy/Sheet';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import SupportRoundedIcon from '@mui/icons-material/SupportRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import {
  VolunteerActivismRounded,
  EmojiEventsRounded,
  AccountCircleRounded,
  SavingsRounded,
  LeaderboardRounded,
  FavoriteRounded,
  PhotoCameraRounded,
  MenuBookRounded,
  PeopleAltRounded,
  AutoAwesome as SadhanaIcon,
  EventNote as EventNoteRounded,
} from '@mui/icons-material';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded';
import SelfImprovementRoundedIcon from '@mui/icons-material/SelfImprovementRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import VolunteerActivismRoundedIcon from '@mui/icons-material/VolunteerActivismRounded';
import MiscellaneousServicesRoundedIcon from '@mui/icons-material/MiscellaneousServicesRounded';
import AutoAwesomeMosaicRoundedIcon from '@mui/icons-material/AutoAwesomeMosaicRounded';
import ColorSchemeToggle from './ColorSchemeToggle';
import LanguageSwitcher from './LanguageSwitcher';
import { closeSidebar } from '../../utils';
import { isProfileComplete } from '../../utils/user';
import { useUserStore } from '../../store/useUserStore';
import { useCongregationMeQuery } from '../../hooks/useCongregationQueries';
import { describeMemberStatus } from '../../utils/congregation';
import { devoteeSections } from '../../pages/congregation/devotee/devoteeWorkspaceConfig';
import { mentorSections } from '../../pages/congregation/mentor/mentorWorkspaceConfig';
import { useRoutePreload } from '../../hooks/useRoutePreload';
import type { CongregationRoleAssignment } from '../../types/congregation';

interface User {
  id: number;
  firebase_uid: string;
  legalName: string;
  email: string;
  phone: string;
  photo_url?: string;
  role: string;
  created_at: string;
  updated_at: string;
}

type SidebarSubLink = {
  label: string;
  path: string;
  icon: React.ElementType;
  match?: 'exact' | 'prefix';
  badge?: string;
};

function Toggler({
  defaultExpanded = false,
  renderToggle,
  children,
  expandWhen = false,
}: {
  defaultExpanded?: boolean;
  children: React.ReactNode;
  renderToggle: (params: {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  }) => React.ReactNode;
  expandWhen?: boolean;
}) {
  const [open, setOpen] = React.useState(defaultExpanded);
  React.useEffect(() => {
    if (expandWhen) {
      setOpen(true);
    }
  }, [expandWhen]);
  return (
    <React.Fragment>
      {renderToggle({ open, setOpen })}
      <Box
        sx={[
          {
            display: 'grid',
            transition: '0.2s ease',
            '& > *': {
              overflow: 'hidden',
              minHeight: 0,
            },
          },
          open ? { gridTemplateRows: '1fr' } : { gridTemplateRows: '0fr' },
        ]}
      >
        <Box sx={{ minHeight: 0 }}>
          {children}
        </Box>
      </Box>
    </React.Fragment>
  );
}

export default function Sidebar() {
  const { t } = useTranslation();
  let navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthorized, setIsAuthorized } = useUserStore();

  // Use new congregation hook - returns null if user is not a member (non-blocking)
  const congregationMeQuery = useCongregationMeQuery({
    staleTime: 60_000,
    refetchOnMount: false,
    retry: 0,
  });

  // IMPORTANT: New hook returns CongregationData | null, not CongregationMeResponse
  // Handle both old and new response structures for backward compatibility
  const rawData = congregationMeQuery.data;

  interface NormalizedCongregationData {
    member: { id?: string | number; name?: string; status?: string } | null;
    roles: CongregationRoleAssignment[];
  }

  const congregationData = React.useMemo<NormalizedCongregationData>(() => {
    if (!rawData) return { member: null, roles: [] };
    const response = rawData as unknown as { member?: { id?: string | number; name?: string; status?: string } | null; roles?: CongregationRoleAssignment[] };
    return {
      member: response.member ?? null,
      roles: response.roles ?? [],
    };
  }, [rawData]);

  const congregationRoles = congregationData?.roles ?? [];
  const hasMentorRole = congregationRoles.some((assignment: CongregationRoleAssignment) => {
    const normalizedRole = assignment.role?.toUpperCase();
    return normalizedRole === 'MENTOR' || normalizedRole === 'FACILITATOR';
  });
  const hasAdminRole = congregationRoles.some(
    (assignment: CongregationRoleAssignment) => assignment.role?.toUpperCase() === 'ADMIN',
  );
  const hasMemberProfile = Boolean(congregationData?.member);
  const memberStatus = congregationData?.member?.status;

  const congregationMenuItems = React.useMemo<SidebarSubLink[]>(() => {
    const items: SidebarSubLink[] = [];
    const normalisedStatus = (memberStatus ?? '').toUpperCase();
    const isAcceptedMember = hasMemberProfile && ['ACTIVE', 'INACTIVE'].includes(normalisedStatus);
    if (isAcceptedMember) {
      // Map section keys to translation keys
      const sectionLabelMap: Record<string, string> = {
        'profile': 'devotee.profile.sectionLabels.personalProfile',
        'family': 'devotee.profile.sectionLabels.familyAndHousehold',
        'batches': 'devotee.profile.sectionLabels.myBatches',
        'books': 'devotee.profile.sectionLabels.bookReading',
        'devotional': 'devotee.profile.sectionLabels.sadhana',
        'services': 'devotee.profile.sectionLabels.services',
      };
      
      devoteeSections.forEach((section) => {
        const path = section.key === 'devotional'
          ? '/congregation/devotee/sadhana'
          : `/congregation/devotee/${section.key}`;
        items.push({
          label: t(sectionLabelMap[section.key] || section.label),
          path,
          icon: section.icon,
          match: 'prefix',
        });
      });
    }
    return items;
  }, [hasMemberProfile, memberStatus, t]);

  const mentorMenuItems = React.useMemo<SidebarSubLink[]>(() => {
    if (!hasMentorRole) {
      return [];
    }

    return [
      { label: t('layout.menu.overview'), path: '/congregation/mentor/overview', icon: SupportAgentRoundedIcon },
      ...mentorSections.map((section) => {
        const path = `/congregation/mentor/${section.key}`;
        return {
          label: t(section.labelKey),
          path,
          icon: section.icon,
          badge: section.key === 'all-devotees' ? t('layout.badges.new') : undefined,
        };
      }),
    ];
  }, [hasMentorRole, t]);

  const adminMenuItems = React.useMemo<SidebarSubLink[]>(() => {
    if (!hasAdminRole) {
      return [];
    }

    return [
      { label: t('layout.menu.devotees'), path: '/congregation/admin/devotees', icon: AdminPanelSettingsRoundedIcon },
      { label: 'Import Members', path: '/congregation/admin/import', icon: CloudUploadRoundedIcon },
      { label: 'Dashboard', path: '/congregation/admin/dashboard', icon: AutoAwesomeMosaicRoundedIcon },
      { label: 'Applications', path: '/congregation/admin/applications', icon: AssignmentRoundedIcon },
      { label: 'All Devotees', path: '/congregation/admin/all-devotees', icon: GroupRoundedIcon },
      { label: 'Mentor Staffing', path: '/congregation/admin/mentor-staffing', icon: ManageAccountsRoundedIcon },
      { label: 'Masters', path: '/congregation/admin/masters', icon: SelfImprovementRoundedIcon },
      { label: 'Batches', path: '/congregation/admin/batches', icon: SchoolRoundedIcon },
      { label: 'Attendance', path: '/congregation/admin/attendance', icon: EventAvailableRoundedIcon },
      { label: 'Services', path: '/congregation/admin/services', icon: VolunteerActivismRoundedIcon },
      { label: 'Service Types', path: '/congregation/admin/service-types', icon: MiscellaneousServicesRoundedIcon },
      { label: 'Books', path: '/congregation/admin/books', icon: AutoStoriesRoundedIcon },
      { label: 'Sadhana', path: '/congregation/admin/sadhana', icon: SelfImprovementRoundedIcon },
    ];
  }, [hasAdminRole, t]);

  const congregationDefaultPath = congregationMenuItems[0]?.path ?? (
    !hasMemberProfile || ['PENDING', 'REJECTED', 'WITHDRAWN'].includes((memberStatus ?? '').toUpperCase())
      ? '/congregation/apply'
      : '/congregation/devotee/profile'
  );
  const mentorDefaultPath = mentorMenuItems[0]?.path ?? '/congregation/mentor/overview';

  const congregationStatusLabel = React.useMemo(
    () => describeMemberStatus(memberStatus) ?? (hasMentorRole ? t('layout.congregationRoles.mentor') : hasMemberProfile ? t('layout.congregationRoles.member') : t('layout.congregationRoles.applicant')),
    [memberStatus, hasMentorRole, hasMemberProfile, t],
  );

  const isActivePath = React.useCallback(
    (path: string, match: SidebarSubLink['match'] = 'prefix') => {
      const normalize = (value: string) => (value === '/' ? '/' : value.replace(/\/+$/, ''));
      const target = normalize(path);
      const current = normalize(location.pathname);

      if (match === 'exact') {
        return current === target;
      }

      if (target === '/') {
        return current === '/';
      }

      return current === target || current.startsWith(`${target}/`);
    },
    [location.pathname],
  );

  const handleRouteChange = React.useCallback(
    (path: string) => {
      navigate(path);
      closeSidebar();
    },
    [navigate],
  );

  const primaryNav = React.useMemo(
    () => [
      { label: t('layout.menu.dashboard'), icon: DashboardRoundedIcon, path: '/', group: 'main' },
      { label: t('layout.menu.calendar'), icon: CalendarMonthRoundedIcon, path: '/calendar', group: 'main' },
      { label: 'Events', icon: EventNoteRounded, path: '/events', group: 'main' },
      { label: t('layout.menu.donate'), icon: VolunteerActivismRounded, path: '/donate', group: 'seva' },
      { label: t('layout.menu.nityaSeva'), icon: FavoriteRounded, path: '/nitya-seva', badge: t('layout.badges.live'), group: 'seva' },
      { label: t('layout.menu.yourContribution'), icon: EmojiEventsRounded, path: '/contribution', group: 'seva' },
      { label: t('layout.menu.fundraising'), icon: SavingsRounded, path: '/fundraising', group: 'seva' },
      { label: t('layout.menu.leaderboard'), icon: LeaderboardRounded, path: '/leaderboard', group: 'seva' },
      { label: t('layout.menu.dailyDarshan'), icon: PhotoCameraRounded, path: '/daily-darshan', group: 'content' },
      { label: t('layout.menu.articles'), icon: MenuBookRounded, path: '/articles', group: 'content' },
      { label: t('layout.menu.profile'), icon: AccountCircleRounded, path: '/profile', group: 'account' },
      { label: t('layout.menu.support'), icon: SupportRoundedIcon, path: '/support', group: 'account' },
    ],
    [t],
  );

  // Initialize route prefetching for better navigation performance
  const prefetch = useRoutePreload({
    delay: 300,
    rootMargin: '50px',
    threshold: 0,
  });

  // React.useEffect(() => {
  //   const checkRole = async () => {
  //     try {
  //       const data = await fetchUserRole();
  //       if ((data as any).role !== "editor" && (data as any).role !== "admin") {
  //         navigate("/unauthorised");
  //       } else {
  //         setIsAuthorized(true);
  //       }
  //     } catch (err) {
  //       console.error("Role check failed:", err);
  //     }
  //   };

  //   checkRole();
  // }, [navigate]);

  // Section collapse state - only Home, Seva, and Community expanded by default
  const [expandedSections, setExpandedSections] = React.useState({
    main: true,
    seva: true,
    content: false,
    community: true,
    admin: true,
    account: false,
  });

  // Collapse seva section for mentor/facilitator/admin users on role change
  React.useEffect(() => {
    if (hasMentorRole || hasAdminRole) {
      setExpandedSections(prev => ({
        ...prev,
        seva: false,
      }));
    }
  }, [hasMentorRole, hasAdminRole]);

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // User data is managed by useUserStore and fetched at app root level
  // Check if profile is complete and redirect if needed
  React.useEffect(() => {
    if (user && !isProfileComplete(user) && location.pathname !== '/profile/complete') {
      navigate('/profile/complete');
    }
  }, [user, location.pathname, navigate]);

  return (
    <Sheet
      className="Sidebar"
      sx={{
        position: 'fixed',
        transform: {
          xs: 'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1)))',
          md: 'none',
        },
        transition: 'transform 0.4s, width 0.4s',
        zIndex: 1000,
        height: '100dvh',
        maxHeight: '100dvh',
        width: 'var(--Sidebar-width)',
        top: 0,
        left: 0,
        px: 1.5,
        py: 1.25,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        borderRight: '1px solid',
        borderColor: 'divider',
        boxSizing: 'border-box',
        background: (theme) => {
          const isDark = theme.palette.mode === 'dark';
          const from = isDark ? 'rgba(16,18,24,0.94)' : 'rgba(250,252,255,0.98)';
          const to = isDark ? 'rgba(16,18,24,0.84)' : 'rgba(240,244,255,0.92)';
          return `linear-gradient(165deg, ${from} 0%, ${to} 100%)`;
        },
      }}
    >
      <GlobalStyles
        styles={(theme) => ({
          ':root': {
            '--Sidebar-width': '220px',
            [theme.breakpoints.up('lg')]: {
              '--Sidebar-width': '240px',
            },
          },
        })}
      />
      <Box
        className="Sidebar-overlay"
        sx={{
          position: 'fixed',
          zIndex: 998,
          top: 0,
          left: 0,
          right: 0,
          width: '100%',
          height: '100dvh',
          opacity: 'var(--SideNavigation-slideIn)',
          backgroundColor: 'var(--joy-palette-background-backdrop)',
          transition: 'opacity 0.4s',
          transform: {
            xs: 'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1) + var(--SideNavigation-slideIn, 0) * var(--Sidebar-width, 0px)))',
            lg: 'translateX(-100%)',
          },
        }}
        onClick={() => closeSidebar()}
      />
      <Sheet
        variant="plain"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 'md',
          p: 0.75,
          display: 'flex',
          alignItems: 'center',
          gap: 0.85,
          border: 'none',
          backgroundColor: 'transparent',
          boxShadow: 'none',
        }}
      >
        <Box
          component="img"
          src="https://cdn.iskconkanpur.org/static/brand/isk-logo-circle.png"
          alt={t('layout.brandAlt')}
          sx={{ width: 36, height: 36, flexShrink: 0 }}
        />
        <Box sx={{ minWidth: 0, flex: 1, position: 'relative', zIndex: 1 }}>
          <Typography
            level="title-sm"
            sx={{ fontWeight: 700, letterSpacing: 0.15, fontSize: '0.8125rem', color: 'text.primary', lineHeight: 1.2 }}
          >
            {t('layout.brandName')}
          </Typography>
        </Box>
        <ColorSchemeToggle
          sx={{
            '--IconButton-size': '30px',
            borderRadius: 'sm',
            boxShadow: 'none',
            backgroundColor: 'background.level1',
            border: '1px solid',
            borderColor: 'neutral.outlinedBorder',
          }}
        />
      </Sheet>
      <Box
        sx={{
          minHeight: 0,
          overflow: 'hidden auto',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          pr: 0.5,
          pb: 0.25,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'neutral.outlinedBorder',
            borderRadius: '3px',
            '&:hover': {
              backgroundColor: 'neutral.outlinedHoverBorder',
            },
          },
        }}
      >
        {/* Main Navigation */}
        <Box>
          <Box
            onClick={() => toggleSection('main')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 0.5,
              cursor: 'pointer',
              borderRadius: 'xs',
              px: 0.75,
              py: 0.3,
              mx: -0.75,
              transition: 'backgroundColor 0.2s ease',
              '&:hover': {
                backgroundColor: 'background.level1',
              },
            }}
          >
            <Typography
              level="body-xs"
              sx={{
                color: 'text.tertiary',
                textTransform: 'uppercase',
                letterSpacing: 0.8,
                fontWeight: 700,
                fontSize: '0.6rem',
              }}
            >
              {t('layout.sections.home')}
            </Typography>
            <KeyboardArrowDownRoundedIcon
              sx={{
                fontSize: '1rem',
                transform: expandedSections.main ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s ease',
                color: 'text.tertiary',
              }}
            />
          </Box>
          {expandedSections.main && (
            <List
              size="sm"
              sx={{
                gap: 0.35,
                '--ListItem-radius': (theme) => theme.vars.radius.md,
              }}
            >
              {primaryNav.filter(item => item.group === 'main').map((item) => {
                const Icon = item.icon;
                const active = isActivePath(item.path);
                return (
                  <ListItem key={item.label} sx={{ '--ListItem-gap': '8px' }}>
                    <ListItemButton
                      onClick={() => handleRouteChange(item.path)}
                      onMouseEnter={(e) => prefetch(e.currentTarget, item.path)}
                      selected={active}
                      sx={[
                        {
                          position: 'relative',
                          borderRadius: 'md',
                          px: 1.25,
                          py: 0.6,
                          gap: 0.85,
                          minHeight: '34px',
                          alignItems: 'center',
                          transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
                          fontWeight: 500,
                          color: 'text.secondary',
                          backgroundColor: 'transparent',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 4,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 2.5,
                            height: '50%',
                            borderRadius: 2,
                            backgroundColor: 'transparent',
                          },
                        },
                        active
                          ? (theme) => ({
                            backgroundColor: theme.vars.palette.background.level1,
                            color: theme.vars.palette.text.primary,
                            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)',
                            transform: 'translateX(4px)',
                            '&::before': {
                              backgroundColor: theme.vars.palette.primary.solidBg,
                            },
                          })
                          : (theme) => ({
                            color: theme.vars.palette.text.primary,
                            '&:hover': {
                              backgroundColor: theme.vars.palette.background.level1,
                              boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
                              transform: 'translateX(4px)',
                              '&::before': {
                                backgroundColor: theme.vars.palette.neutral.outlinedColor,
                              },
                            },
                          }),
                      ]}
                    >
                      <ListItemDecorator
                        sx={{
                          color: active ? 'inherit' : 'neutral.600',
                          transition: 'color 0.2s ease',
                          minWidth: '20px',
                          mr: 0.2,
                        }}
                      >
                        <Icon sx={{ fontSize: '1.15rem' }} />
                      </ListItemDecorator>
                      <ListItemContent>
                        <Typography level="body-sm" sx={{ fontWeight: active ? 600 : 500, fontSize: '0.875rem' }}>
                          {item.label}
                        </Typography>
                      </ListItemContent>
                      {item.badge ? (
                        <Chip
                          size="sm"
                          variant={active ? 'solid' : 'soft'}
                          color="primary"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.65rem',
                            px: 0.75,
                            height: '18px',
                            minHeight: '18px',
                            borderRadius: 'xs',
                            boxShadow: active ? 'var(--joy-shadow-xs)' : 'none',
                          }}
                        >
                          {item.badge}
                        </Chip>
                      ) : null}
                    </ListItemButton>
                  </ListItem>
                );
              })}
              {!hasMemberProfile || ['PENDING', 'REJECTED', 'WITHDRAWN'].includes((memberStatus ?? '').toUpperCase()) ? (
                <ListItem sx={{ '--ListItem-gap': '8px' }}>
                  <ListItemButton
                    onClick={() => handleRouteChange('/congregation/apply')}
                    onMouseEnter={(e) => prefetch(e.currentTarget, '/congregation/apply')}
                    selected={isActivePath('/congregation/apply')}
                    sx={[
                      {
                        position: 'relative',
                        borderRadius: 'md',
                        px: 1.25,
                        py: 0.6,
                        gap: 0.85,
                        minHeight: '34px',
                        alignItems: 'center',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
                        fontWeight: 500,
                        color: 'text.secondary',
                        backgroundColor: 'transparent',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 4,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: 2.5,
                          height: '50%',
                          borderRadius: 2,
                          backgroundColor: 'transparent',
                        },
                      },
                      isActivePath('/congregation/apply')
                        ? (theme) => ({
                          backgroundColor: theme.vars.palette.background.level1,
                          color: theme.vars.palette.text.primary,
                          boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)',
                          transform: 'translateX(4px)',
                          '&::before': {
                            backgroundColor: theme.vars.palette.primary.solidBg,
                          },
                        })
                        : (theme) => ({
                          color: theme.vars.palette.text.primary,
                          '&:hover': {
                            backgroundColor: theme.vars.palette.background.level1,
                            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
                            transform: 'translateX(4px)',
                            '&::before': {
                              backgroundColor: theme.vars.palette.neutral.outlinedColor,
                            },
                          },
                        }),
                    ]}
                  >
                    <ListItemDecorator
                      sx={{
                        color: isActivePath('/congregation/apply') ? 'inherit' : 'neutral.600',
                        transition: 'color 0.2s ease',
                        minWidth: '20px',
                        mr: 0.2,
                      }}
                    >
                      <PendingActionsRoundedIcon sx={{ fontSize: '1.15rem' }} />
                    </ListItemDecorator>
                    <ListItemContent>
                      <Typography level="body-sm" sx={{ fontWeight: isActivePath('/congregation/apply') ? 600 : 500, fontSize: '0.875rem' }}>
                        {t('layout.menu.application')}
                      </Typography>
                    </ListItemContent>
                  </ListItemButton>
                </ListItem>
              ) : null}
              {congregationMenuItems.length ? (
                <Toggler
                  defaultExpanded={false}
                  expandWhen={location.pathname.startsWith('/congregation')}
                  renderToggle={({ open, setOpen }) => {
                    const active =
                      location.pathname.startsWith('/congregation/apply') ||
                      location.pathname.startsWith('/congregation/devotee');
                    return (
                      <ListItem sx={{ '--ListItem-gap': '8px' }}>
                        <ListItemButton
                          onClick={() => {
                            const willOpen = !open;
                            setOpen(willOpen);
                            if (willOpen && !active && congregationDefaultPath) {
                              handleRouteChange(congregationDefaultPath);
                            }
                          }}
                          selected={active}
                          sx={[
                            {
                              position: 'relative',
                              borderRadius: 'md',
                              px: 1.25,
                              py: 0.6,
                              gap: 0.85,
                              minHeight: '34px',
                              alignItems: 'center',
                              transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
                              fontWeight: 500,
                              color: 'text.secondary',
                              backgroundColor: 'transparent',
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 4,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: 2.5,
                                height: '50%',
                                borderRadius: 2,
                                backgroundColor: 'transparent',
                              },
                            },
                            active
                              ? (theme: any) => ({
                                backgroundColor: theme.vars.palette.background.level1,
                                color: theme.vars.palette.text.primary,
                                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)',
                                transform: 'translateX(4px)',
                                '&::before': {
                                  backgroundColor: theme.vars.palette.primary.solidBg,
                                },
                              })
                              : (theme: any) => ({
                                color: theme.vars.palette.text.primary,
                                '&:hover': {
                                  backgroundColor: theme.vars.palette.background.level1,
                                  boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
                                  transform: 'translateX(4px)',
                                  '&::before': {
                                    backgroundColor: theme.vars.palette.neutral.outlinedColor,
                                  },
                                },
                              }),
                          ]}
                        >
                          <ListItemDecorator
                            sx={{
                              color: active ? 'inherit' : 'neutral.600',
                              transition: 'color 0.2s ease',
                              minWidth: '20px',
                              mr: 0.2,
                            }}
                          >
                            <PeopleAltRounded sx={{ fontSize: '1.15rem' }} />
                          </ListItemDecorator>
                          <ListItemContent>
                            <Typography level="body-sm" sx={{ fontWeight: active ? 600 : 500, fontSize: '0.875rem' }}>
                              {t('layout.menu.congregation')}
                            </Typography>
                          </ListItemContent>
                          <KeyboardArrowDownRoundedIcon
                            sx={{
                              fontSize: '1rem',
                              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s ease',
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  }}
                >
                  <List
                    size="sm"
                    sx={{
                      mt: 0.25,
                      gap: 0.25,
                      pl: 0.5,
                      '--ListItem-radius': (theme: any) => theme.vars.radius.sm,
                    }}
                  >
                    {congregationMenuItems.map((link) => {
                      const LinkIcon = link.icon;
                      const active = isActivePath(link.path, link.match);
                      return (
                        <ListItem key={link.label} sx={{ '--ListItem-gap': '8px' }}>
                          <ListItemButton
                            onClick={() => handleRouteChange(link.path)}
                            selected={active}
                            sx={[
                              {
                                position: 'relative',
                                borderRadius: 'sm',
                                px: 1.1,
                                py: 0.5,
                                minHeight: '30px',
                                gap: 0.75,
                                alignItems: 'center',
                                pl: 1.8,
                                transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
                                fontWeight: 500,
                                color: 'text.secondary',
                                backgroundColor: 'transparent',
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  left: 4,
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  width: 2,
                                  height: '40%',
                                  borderRadius: 1,
                                  backgroundColor: 'transparent',
                                },
                              },
                              active
                                ? (theme: any) => ({
                                  backgroundColor: theme.vars.palette.background.level2,
                                  color: theme.vars.palette.text.primary,
                                  transform: 'translateX(2px)',
                                  '&::before': {
                                    backgroundColor: theme.vars.palette.primary.solidBg,
                                  },
                                })
                                : (theme: any) => ({
                                  color: theme.vars.palette.text.primary,
                                  '&:hover': {
                                    backgroundColor: theme.vars.palette.background.level1,
                                    transform: 'translateX(2px)',
                                    '&::before': {
                                      backgroundColor: theme.vars.palette.neutral.outlinedColor,
                                    },
                                  },
                                }),
                            ]}
                          >
                            <ListItemDecorator sx={{ color: active ? 'primary.500' : 'neutral.600', minWidth: '18px', mr: 0.2, }}>
                              <LinkIcon sx={{ fontSize: '1rem' }} />
                            </ListItemDecorator>
                            <ListItemContent>
                              <Typography level="body-sm" sx={{ fontWeight: active ? 600 : 500, fontSize: '0.8125rem' }}>
                                {link.label}
                              </Typography>
                            </ListItemContent>
                            {link.badge ? (
                              <Chip
                                size="sm"
                                variant={active ? 'solid' : 'soft'}
                                color="primary"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '0.6rem',
                                  px: 0.6,
                                  height: '16px',
                                  minHeight: '16px',
                                  borderRadius: 'xs',
                                  boxShadow: active ? 'var(--joy-shadow-xs)' : 'none',
                                }}
                              >
                                {link.badge}
                              </Chip>
                            ) : null}
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Toggler>
              ) : null}
            </List>
          )}
        </Box>

        {/* Seva Section */}
        <Box>
          <Box
            onClick={() => toggleSection('seva')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 0.5,
              cursor: 'pointer',
              borderRadius: 'xs',
              px: 0.75,
              py: 0.3,
              mx: -0.75,
              transition: 'backgroundColor 0.2s ease',
              '&:hover': {
                backgroundColor: 'background.level1',
              },
            }}
          >
            <Typography
              level="body-xs"
              sx={{
                color: 'text.tertiary',
                textTransform: 'uppercase',
                letterSpacing: 0.8,
                fontWeight: 700,
                fontSize: '0.6rem',
              }}
            >
              {t('layout.sections.seva')}
            </Typography>
            <KeyboardArrowDownRoundedIcon
              sx={{
                fontSize: '1rem',
                transform: expandedSections.seva ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s ease',
                color: 'text.tertiary',
              }}
            />
          </Box>
          {expandedSections.seva && (
            <>
              <Divider sx={{ my: 0.25, opacity: 0.4 }} />
              <List
                size="sm"
                sx={{
                  gap: 0.35,
                  '--ListItem-radius': (theme) => theme.vars.radius.md,
                }}
              >
              {primaryNav.filter(item => item.group === 'seva').map((item) => {
                const Icon = item.icon;
                const active = isActivePath(item.path);
                return (
                  <ListItem key={item.label} sx={{ '--ListItem-gap': '8px' }}>
                    <ListItemButton
                      onClick={() => handleRouteChange(item.path)}
                      onMouseEnter={(e) => prefetch(e.currentTarget, item.path)}
                      selected={active}
                      sx={[
                        {
                          position: 'relative',
                          borderRadius: 'md',
                          px: 1.1,
                          py: 0.5,
                          gap: 0.75,
                          minHeight: '32px',
                          alignItems: 'center',
                          transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
                          fontWeight: 500,
                          color: 'text.secondary',
                          backgroundColor: 'transparent',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 4,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 2.5,
                            height: '50%',
                            borderRadius: 2,
                            backgroundColor: 'transparent',
                          },
                        },
                        active
                          ? (theme) => ({
                            backgroundColor: theme.vars.palette.background.level1,
                            color: theme.vars.palette.text.primary,
                            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)',
                            transform: 'translateX(4px)',
                            '&::before': {
                              backgroundColor: theme.vars.palette.primary.solidBg,
                            },
                          })
                          : (theme) => ({
                            color: theme.vars.palette.text.primary,
                            '&:hover': {
                              backgroundColor: theme.vars.palette.background.level1,
                              boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
                              transform: 'translateX(4px)',
                              '&::before': {
                                backgroundColor: theme.vars.palette.neutral.outlinedColor,
                              },
                            },
                          }),
                      ]}
                    >
                      <ListItemDecorator
                        sx={{
                          color: active ? 'inherit' : 'neutral.600',
                          transition: 'color 0.2s ease',
                          minWidth: '20px',
                          mr: 0.2,
                        }}
                      >
                        <Icon sx={{ fontSize: '1.15rem' }} />
                      </ListItemDecorator>
                      <ListItemContent>
                        <Typography level="body-sm" sx={{ fontWeight: active ? 600 : 500, fontSize: '0.875rem' }}>
                          {item.label}
                        </Typography>
                      </ListItemContent>
                      {item.badge ? (
                        <Chip
                          size="sm"
                          variant={active ? 'solid' : 'soft'}
                          color="primary"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.65rem',
                            px: 0.75,
                            height: '18px',
                            minHeight: '18px',
                            borderRadius: 'xs',
                            boxShadow: active ? 'var(--joy-shadow-xs)' : 'none',
                          }}
                        >
                          {item.badge}
                        </Chip>
                      ) : null}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
            </>
          )}
        </Box>

        {/* Community Section - Only for Mentor/Facilitator */}
        {hasMentorRole && (
        <Box>
          <Box
            onClick={() => toggleSection('community')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 0.5,
              cursor: 'pointer',
              borderRadius: 'xs',
              px: 0.75,
              py: 0.3,
              mx: -0.75,
              transition: 'backgroundColor 0.2s ease',
              '&:hover': {
                backgroundColor: 'background.level1',
              },
            }}
          >
            <Typography
              level="body-xs"
              sx={{
                color: 'text.tertiary',
                textTransform: 'uppercase',
                letterSpacing: 0.8,
                fontWeight: 700,
                fontSize: '0.6rem',
              }}
            >
              {t('layout.sections.community')}
            </Typography>
            <KeyboardArrowDownRoundedIcon
              sx={{
                fontSize: '1rem',
                transform: expandedSections.community ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s ease',
                color: 'text.tertiary',
              }}
            />
          </Box>
          {expandedSections.community && (
            <>
              <Divider sx={{ my: 0.25, opacity: 0.4 }} />
              <List
              size="sm"
              sx={{
                gap: 0.35,
                '--ListItem-radius': (theme) => theme.vars.radius.md,
              }}
            >
              {mentorMenuItems.length ? (
                <Toggler
                  defaultExpanded={false}
                  expandWhen={location.pathname.startsWith('/congregation/mentor')}
                  renderToggle={({ open, setOpen }) => {
                    const active = location.pathname.startsWith('/congregation/mentor');
                    return (
                      <ListItem sx={{ '--ListItem-gap': '8px' }}>
                        <ListItemButton
                          onClick={() => {
                            const willOpen = !open;
                            setOpen(!open);
                            if (willOpen && !active && mentorDefaultPath) {
                              handleRouteChange(mentorDefaultPath);
                            }
                          }}
                          selected={active}
                          sx={{
                            borderRadius: 'md',
                            px: 1.1,
                            py: 0.5,
                            gap: 0.75,
                            minHeight: '32px',
                            alignItems: 'center',
                          }}
                        >
                          <ListItemDecorator sx={{ minWidth: '20px', mr: 0.2, }}>
                            <SupportAgentRoundedIcon sx={{ fontSize: '1.1rem' }} />
                          </ListItemDecorator>
                          <ListItemContent>
                            <Typography level="body-sm" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                              {t('layout.menu.mentor')}
                            </Typography>
                          </ListItemContent>
                          <KeyboardArrowDownRoundedIcon
                            sx={{
                              fontSize: '1rem',
                              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s ease',
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  }}
                >
                  <List
                    size="sm"
                    sx={{
                      mt: 0.25,
                      gap: 0.25,
                      pl: 0.5,
                      '--ListItem-radius': (theme) => theme.vars.radius.sm,
                    }}
                  >
                    {mentorMenuItems.map((link) => {
                      const LinkIcon = link.icon;
                      const active = isActivePath(link.path, link.match);
                      return (
                        <ListItem key={link.label} sx={{ '--ListItem-gap': '8px' }}>
                          <ListItemButton
                            onClick={() => handleRouteChange(link.path)}
                            selected={active}
                            sx={{
                              borderRadius: 'sm',
                              px: 1.25,
                              py: 0.5,
                              minHeight: '30px',
                              gap: 0.75,
                              alignItems: 'center',
                              pl: 2.5,
                              justifyContent: 'space-between',
                            }}
                          >
                            <ListItemDecorator sx={{ color: active ? 'primary.500' : 'neutral.600', minWidth: '18px', mr: 0.2, }}>
                              <LinkIcon sx={{ fontSize: '1rem' }} />
                            </ListItemDecorator>
                            <ListItemContent sx={{ minWidth: 0, flex: 1 }}>
                              <Typography
                                level="body-sm"
                                noWrap
                                sx={{ fontWeight: active ? 600 : 500, fontSize: '0.8125rem' }}
                              >
                                {link.label}
                              </Typography>
                            </ListItemContent>
                            {link.badge ? (
                              <Chip
                                size="sm"
                                variant={active ? 'solid' : 'soft'}
                                color="primary"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '0.6rem',
                                  px: 0.6,
                                  height: '16px',
                                  minHeight: '16px',
                                  borderRadius: 'xs',
                                  boxShadow: active ? 'var(--joy-shadow-xs)' : 'none',
                                  ml: 'auto',
                                }}
                              >
                                {link.badge}
                              </Chip>
                            ) : null}
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Toggler>
              ) : null}
            </List>
            </>
          )}
        </Box>
        )}

        {/* Admin Section - Only for Admin role */}
        {hasAdminRole && (
        <Box>
          <Box
            onClick={() => toggleSection('admin')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 0.5,
              cursor: 'pointer',
              borderRadius: 'xs',
              px: 0.75,
              py: 0.3,
              mx: -0.75,
              transition: 'backgroundColor 0.2s ease',
              '&:hover': { backgroundColor: 'background.level1' },
            }}
          >
            <Typography
              level="body-xs"
              sx={{
                color: 'text.tertiary',
                textTransform: 'uppercase',
                letterSpacing: 0.8,
                fontWeight: 700,
                fontSize: '0.6rem',
              }}
            >
              Admin
            </Typography>
            <KeyboardArrowDownRoundedIcon
              sx={{
                fontSize: '1rem',
                transform: expandedSections.admin ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s ease',
                color: 'text.tertiary',
              }}
            />
          </Box>
          {expandedSections.admin && (
            <>
              <Divider sx={{ my: 0.25, opacity: 0.4 }} />
              <List
                size="sm"
                sx={{
                  gap: 0.25,
                  '--ListItem-radius': (theme) => theme.vars.radius.sm,
                }}
              >
                {adminMenuItems.map((link) => {
                  const LinkIcon = link.icon;
                  const active = isActivePath(link.path, link.match);
                  return (
                    <ListItem key={link.path} sx={{ '--ListItem-gap': '8px' }}>
                      <ListItemButton
                        onClick={() => handleRouteChange(link.path)}
                        selected={active}
                        sx={{
                          borderRadius: 'sm',
                          px: 1.1,
                          py: 0.5,
                          minHeight: '30px',
                          gap: 0.75,
                          alignItems: 'center',
                        }}
                      >
                        <ListItemDecorator sx={{ color: active ? 'primary.500' : 'neutral.600', minWidth: '18px', mr: 0.2 }}>
                          <LinkIcon sx={{ fontSize: '1rem' }} />
                        </ListItemDecorator>
                        <ListItemContent>
                          <Typography level="body-sm" sx={{ fontWeight: active ? 600 : 500, fontSize: '0.8125rem' }}>
                            {link.label}
                          </Typography>
                        </ListItemContent>
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}
        </Box>
        )}

        {/* Content Section */}
        <Box>
          <Box
            onClick={() => toggleSection('content')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 0.5,
              cursor: 'pointer',
              borderRadius: 'xs',
              px: 0.75,
              py: 0.3,
              mx: -0.75,
              transition: 'backgroundColor 0.2s ease',
              '&:hover': {
                backgroundColor: 'background.level1',
              },
            }}
          >
            <Typography
              level="body-xs"
              sx={{
                color: 'text.tertiary',
                textTransform: 'uppercase',
                letterSpacing: 0.8,
                fontWeight: 700,
                fontSize: '0.6rem',
              }}
            >
              {t('layout.sections.resources')}
            </Typography>
            <KeyboardArrowDownRoundedIcon
              sx={{
                fontSize: '1rem',
                transform: expandedSections.content ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s ease',
                color: 'text.tertiary',
              }}
            />
          </Box>
          {expandedSections.content && (
            <>
              <Divider sx={{ my: 0.25, opacity: 0.4 }} />
              <List
                size="sm"
                sx={{
                  gap: 0.35,
                  '--ListItem-radius': (theme) => theme.vars.radius.md,
                }}
              >
              {primaryNav.filter(item => item.group === 'content').map((item) => {
                const Icon = item.icon;
                const active = isActivePath(item.path);
                return (
                  <ListItem key={item.label} sx={{ '--ListItem-gap': '8px' }}>
                    <ListItemButton
                      onClick={() => handleRouteChange(item.path)}
                      onMouseEnter={(e) => prefetch(e.currentTarget, item.path)}
                      selected={active}
                      sx={[
                        {
                          position: 'relative',
                          borderRadius: 'md',
                          px: 1.1,
                          py: 0.5,
                          gap: 0.75,
                          minHeight: '32px',
                          alignItems: 'center',
                          transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
                          fontWeight: 500,
                          color: 'text.secondary',
                          backgroundColor: 'transparent',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 4,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 2.5,
                            height: '50%',
                            borderRadius: 2,
                            backgroundColor: 'transparent',
                          },
                        },
                        active
                          ? (theme) => ({
                            backgroundColor: theme.vars.palette.background.level1,
                            color: theme.vars.palette.text.primary,
                            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)',
                            transform: 'translateX(4px)',
                            '&::before': {
                              backgroundColor: theme.vars.palette.primary.solidBg,
                            },
                          })
                          : (theme) => ({
                            color: theme.vars.palette.text.primary,
                            '&:hover': {
                              backgroundColor: theme.vars.palette.background.level1,
                              boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
                              transform: 'translateX(4px)',
                              '&::before': {
                                backgroundColor: theme.vars.palette.neutral.outlinedColor,
                              },
                            },
                          }),
                      ]}
                    >
                      <ListItemDecorator
                        sx={{
                          color: active ? 'inherit' : 'neutral.600',
                          transition: 'color 0.2s ease',
                          minWidth: '20px',
                          mr: 0.2,
                        }}
                      >
                        <Icon sx={{ fontSize: '1.15rem' }} />
                      </ListItemDecorator>
                      <ListItemContent>
                        <Typography level="body-sm" sx={{ fontWeight: active ? 600 : 500, fontSize: '0.875rem' }}>
                          {item.label}
                        </Typography>
                      </ListItemContent>
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
            </>
          )}
        </Box>

        {/* Account Section */}
        <Box>
          <Box
            onClick={() => toggleSection('account')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 0.5,
              cursor: 'pointer',
              borderRadius: 'xs',
              px: 0.75,
              py: 0.3,
              mx: -0.75,
              transition: 'backgroundColor 0.2s ease',
              '&:hover': {
                backgroundColor: 'background.level1',
              },
            }}
          >
            <Typography
              level="body-xs"
              sx={{
                color: 'text.tertiary',
                textTransform: 'uppercase',
                letterSpacing: 0.8,
                fontWeight: 700,
                fontSize: '0.6rem',
              }}
            >
              {t('layout.sections.account')}
            </Typography>
            <KeyboardArrowDownRoundedIcon
              sx={{
                fontSize: '1rem',
                transform: expandedSections.account ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s ease',
                color: 'text.tertiary',
              }}
            />
          </Box>
          {expandedSections.account && (
            <>
              <Divider sx={{ my: 0.25, opacity: 0.4 }} />
              <List
                size="sm"
                sx={{
                  gap: 0.35,
                  '--ListItem-radius': (theme) => theme.vars.radius.md,
                }}
              >
              {primaryNav.filter(item => item.group === 'account').map((item) => {
                const Icon = item.icon;
                const active = isActivePath(item.path);
                return (
                  <ListItem key={item.label} sx={{ '--ListItem-gap': '8px' }}>
                    <ListItemButton
                      onClick={() => handleRouteChange(item.path)}
                      onMouseEnter={(e) => prefetch(e.currentTarget, item.path)}
                      selected={active}
                      sx={[
                        {
                          position: 'relative',
                          borderRadius: 'md',
                          px: 1.1,
                          py: 0.5,
                          gap: 0.75,
                          minHeight: '32px',
                          alignItems: 'center',
                          transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
                          fontWeight: 500,
                          color: 'text.secondary',
                          backgroundColor: 'transparent',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 4,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 2.5,
                            height: '50%',
                            borderRadius: 2,
                            backgroundColor: 'transparent',
                          },
                        },
                        active
                          ? (theme) => ({
                            backgroundColor: theme.vars.palette.background.level1,
                            color: theme.vars.palette.text.primary,
                            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)',
                            transform: 'translateX(4px)',
                            '&::before': {
                              backgroundColor: theme.vars.palette.primary.solidBg,
                            },
                          })
                          : (theme) => ({
                            color: theme.vars.palette.text.primary,
                            '&:hover': {
                              backgroundColor: theme.vars.palette.background.level1,
                              boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
                              transform: 'translateX(4px)',
                              '&::before': {
                                backgroundColor: theme.vars.palette.neutral.outlinedColor,
                              },
                            },
                          }),
                      ]}
                    >
                      <ListItemDecorator
                        sx={{
                          color: active ? 'inherit' : 'neutral.600',
                          transition: 'color 0.2s ease',
                          minWidth: '20px',
                          mr: 0.2,
                        }}
                      >
                        <Icon sx={{ fontSize: '1.15rem' }} />
                      </ListItemDecorator>
                      <ListItemContent>
                        <Typography level="body-sm" sx={{ fontWeight: active ? 600 : 500, fontSize: '0.875rem' }}>
                          {item.label}
                        </Typography>
                      </ListItemContent>
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
            </>
          )}
        </Box>
      </Box>

      <Sheet
        variant="plain"
        sx={{
          mt: 0.35,
          pt: 0.75,
          pb: 0.65,
          px: 1.15,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.65,
          borderTop: '1px solid',
          borderColor: 'divider',
          position: 'relative',
          background: 'transparent',
        }}
      >
        <Box sx={{ mb: 1 }}>
          <LanguageSwitcher compact={false} />
        </Box>
        <Box
          sx={{
            px: 1,
            py: 0.75,
            backgroundColor: 'background.body',
            borderRadius: 'md',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar
              variant="soft"
              color="primary"
              sx={{
                width: 32,
                height: 32,
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
              src={user?.photo_url || ''}
            >
              {user?.legalName?.charAt(0) || 'V'}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography level="title-sm" sx={{ fontWeight: 600, fontSize: '0.8125rem' }} noWrap>
                {user?.legalName || t('layout.menu.valuedDevotee')}
              </Typography>
              <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.6875rem' }} noWrap>
                {user?.email || t('layout.menu.sevaInProgress')}
              </Typography>
            </Box>
          </Stack>
        </Box>
        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          endDecorator={<LogoutRoundedIcon sx={{ fontSize: '0.95rem' }} />}
          onClick={() => {
            const redirectUrl = encodeURIComponent(window.location.href);
            window.location.href = `${process.env.REACT_APP_AUTH_WEB_URL}/signout?redirect=${redirectUrl}`;
          }}
          sx={{
            width: '100%',
            borderRadius: 'md',
            px: 1,
            py: 0.5,
            fontSize: '0.75rem',
            fontWeight: 500,
            minHeight: '32px',
            borderColor: 'divider',
            '&:hover': {
              backgroundColor: 'background.level1',
              borderColor: 'neutral.outlinedHoverBorder',
            },
          }}
        >
          {t('layout.menu.signOut')}
        </Button>
      </Sheet>
    </Sheet>
  );
}
